/**
 * Server state for receipts.
 *
 * Same pattern as the settings screens: reads are `queryOptions`, writes are mutations that
 * invalidate what they changed, and nothing copies server data into React state.
 *
 * The detail query polls itself while the worker is extracting. That is the whole of how the
 * capture screen learns extraction finished -- there is no jobs endpoint to ask (CLAUDE.md,
 * "Async work").
 */

import { useRef } from "react";
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import type { components } from "@/api/schema";
import { toLineIn, type EditLine } from "./review";
import type { ManualReceiptBody } from "./schemas";
import { pollInterval } from "./status";

export type Receipt = components["schemas"]["ReceiptOut"];
export type ReceiptSummary = components["schemas"]["ReceiptSummaryOut"];
export type Allocation = components["schemas"]["AllocationOut"];
export type AllocationsBody = components["schemas"]["AllocationsIn"];

// --- reads --------------------------------------------------------------------------------------

/** The list, a page at a time, following the cursor the API hands back. */
export const receiptsQuery = infiniteQueryOptions({
  queryKey: ["receipts"],
  queryFn: async ({ pageParam }) =>
    unwrap(
      await api.GET("/api/v1/receipts", {
        params: { query: pageParam ? { cursor: pageParam } : {} },
      }),
    ),
  initialPageParam: "",
  getNextPageParam: (page) => page.next_cursor ?? undefined,
});

export function receiptQuery(receiptId: string) {
  return queryOptions({
    queryKey: ["receipt", receiptId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/receipts/{receipt_id}", {
          params: { path: { receipt_id: receiptId } },
        }),
      ),
    refetchInterval: (query) => pollInterval(query.state.data?.status, query.state.dataUpdateCount),
    // Keep polling while the phone's screen is off or the tab is in the background: the user
    // put the phone down to wait, and should find the result when they pick it up.
    refetchIntervalInBackground: true,
  });
}

/**
 * A signed URL for the receipt's image.
 *
 * Fetched only when the image is about to be shown, because every URL is a credential that
 * lasts minutes. Treated as stale well before it expires, so a page left open does not end up
 * holding a URL that storage has stopped honouring.
 */
export function imageUrlQuery(receiptId: string) {
  return queryOptions({
    queryKey: ["receipt", receiptId, "image-url"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/receipts/{receipt_id}/image-url", {
          params: { path: { receipt_id: receiptId } },
        }),
      ),
    staleTime: 60_000,
    gcTime: 60_000,
  });
}

// --- writes -------------------------------------------------------------------------------------

export function useStartUpload() {
  return useMutation({
    mutationFn: async (contentType: string) =>
      unwrap(
        await api.POST("/api/v1/receipts/uploads", {
          // A screenshot and a photograph look the same from here, and nothing downstream
          // treats them differently yet, so every capture is recorded as a photo.
          body: { content_type: contentType, source: "photo" },
        }),
      ),
  });
}

export function useCompleteUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (receiptId: string) =>
      unwrap(
        await api.POST("/api/v1/receipts/{receipt_id}/complete", {
          params: { path: { receipt_id: receiptId } },
        }),
      ),
    onSuccess: async (receipt) => {
      // Seed the detail page with the answer already in hand, so it opens on "extracting"
      // instead of a spinner, and starts polling from there.
      queryClient.setQueryData(receiptQuery(receipt.id).queryKey, receipt);
      await queryClient.invalidateQueries({ queryKey: ["receipts"] });
    },
  });
}

// --- review -------------------------------------------------------------------------------------

/**
 * Save the review screen's whole list of lines.
 *
 * Optimistic: the list on screen changes at once, and the server's answer replaces it when it
 * comes. Saves may overlap -- tapping one line's bucket, then the next -- so an answer is applied
 * only if no later save has started since; otherwise it would put back the earlier state. A
 * failed save refetches the receipt, so the screen shows what the server holds.
 */
export function useSaveLines(receiptId: string) {
  const queryClient = useQueryClient();
  const queryKey = receiptQuery(receiptId).queryKey;
  const latest = useRef(0);
  return useMutation({
    mutationFn: async (lines: EditLine[]) =>
      unwrap(
        await api.PUT("/api/v1/receipts/{receipt_id}/lines", {
          params: { path: { receipt_id: receiptId } },
          body: { lines: lines.map(toLineIn) },
        }),
      ),
    onMutate: async (lines) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (old) => (old ? { ...old, lines } : old));
      latest.current += 1;
      return { save: latest.current };
    },
    onSuccess: async (receipt, _lines, context) => {
      if (context.save === latest.current) queryClient.setQueryData(queryKey, receipt);
      // The server settles a line's orders again when the line changes: a new amount re-splits
      // them, a new bucket drops them. So what each order cost may have moved too.
      await afterAllocations(queryClient, receiptId);
    },
    onError: () => queryClient.invalidateQueries({ queryKey }),
  });
}

/** The orders each of the receipt's lines is attached to, with each order's share. */
export function receiptAllocationsQuery(receiptId: string) {
  return queryOptions({
    queryKey: ["receipt", receiptId, "allocations"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/receipts/{receipt_id}/allocations", {
          params: { path: { receipt_id: receiptId } },
        }),
      ),
  });
}

function afterAllocations(queryClient: QueryClient, receiptId: string) {
  // ["order"] covers every order's purchases and money, whichever orders the line left or joined.
  return Promise.all(
    [["receipt", receiptId, "allocations"], ["order"]].map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  );
}

/** Attach one line to these orders, replacing what it was attached to; none detaches it. */
export function useSetAllocations(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ lineId, body }: { lineId: string; body: AllocationsBody }) =>
      unwrap(
        await api.PUT("/api/v1/receipt-lines/{line_id}/allocations", {
          params: { path: { line_id: lineId } },
          body,
        }),
      ),
    onSuccess: () => afterAllocations(queryClient, receiptId),
  });
}

export type ReceiptChanges = components["schemas"]["ReceiptPatchIn"];

export function useUpdateReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ReceiptChanges) =>
      unwrap(
        await api.PATCH("/api/v1/receipts/{receipt_id}", {
          params: { path: { receipt_id: receiptId } },
          body,
        }),
      ),
    onSuccess: async (receipt) => {
      queryClient.setQueryData(receiptQuery(receiptId).queryKey, receipt);
      await queryClient.invalidateQueries({ queryKey: ["receipts"] });
    },
  });
}

export function useConfirmReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (acceptMismatch: boolean) =>
      unwrap(
        await api.POST("/api/v1/receipts/{receipt_id}/confirm", {
          params: { path: { receipt_id: receiptId } },
          body: { accept_mismatch: acceptMismatch },
        }),
      ),
    onSuccess: async (receipt) => {
      queryClient.setQueryData(receiptQuery(receiptId).queryKey, receipt);
      // Confirming named every line, so the catalog has new products and lessons.
      await Promise.all(
        ["receipts", "products", "stores", "store-products"].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] }),
        ),
      );
    },
  });
}

export function useReopenReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/v1/receipts/{receipt_id}/reopen", {
          params: { path: { receipt_id: receiptId } },
        }),
      ),
    onSuccess: async (receipt) => {
      queryClient.setQueryData(receiptQuery(receiptId).queryKey, receipt);
      await queryClient.invalidateQueries({ queryKey: ["receipts"] });
    },
  });
}

export function useCreateManualReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ManualReceiptBody) =>
      unwrap(await api.POST("/api/v1/receipts", { body })),
    onSuccess: async (receipt) => {
      queryClient.setQueryData(receiptQuery(receipt.id).queryKey, receipt);
      await queryClient.invalidateQueries({ queryKey: ["receipts"] });
    },
  });
}

// --- photos a downgrade left behind -------------------------------------------------------------

export type Photos = components["schemas"]["PhotosOut"];

/** How many photos are kept, how many a downgrade left waiting, and whether everything has been
 * downloaded so they can go. */
export const photosQuery = queryOptions({
  queryKey: ["receipt-photos"],
  queryFn: async () => unwrap(await api.GET("/api/v1/receipt-photos")),
});

export function useDeletePhotos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (skipExport: boolean) =>
      unwrap(
        await api.POST("/api/v1/receipt-photos/delete", { body: { skip_export: skipExport } }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["receipt-photos"] });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
